/**
 * Requests Center — everything you are waiting on a carrier for, in one place.
 *
 * Josh, 2026-09-05: "If we did the requests together, we might call it the
 * Requests Center." Loss Runs, Report a Loss and Binder Requests. The Submission
 * Portal and Policy Upload are each their own thing and deliberately absent —
 * they send work to Agents Alliance rather than asking a carrier for something
 * back, so nothing on them is ever "outstanding with a market".
 *
 * ⚠️ THIS IS A VIEW, NOT A MERGE. The three programmes keep their own tables,
 * forms, chase rules and pages; this reads across them. Merging the data would
 * have meant rewriting three working features to answer one question — and the
 * question is "what am I waiting on", which a read answers perfectly well.
 *
 * ⚠️ AND IT NEVER RANKS THE THREE CLOCKS AGAINST EACH OTHER. "Waiting 11 days",
 * "unacknowledged 4 days" and "incepts tomorrow" are not comparable numbers.
 * Rows group by severity — late, then soon, then the rest — and each carries its
 * own sentence. Sorting the whole list by one number would have to decide
 * whether a fortnight-old loss run beats a binder incepting in the morning, and
 * there is no honest answer to that.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { StatCard } from '../components/StatCard';
import { HUB_MODULES } from '@commpass/shared';
import { ApiError, api, type UnifiedRequest } from '../lib/api';
import { newFormHref } from '../lib/newForm';
import { useReceipt } from '../lib/receipt';
import { RequestThread } from '../components/RequestThread';
import { UnmatchedQueue } from '../components/UnmatchedQueue';
import { CarrierPerformance } from '../components/CarrierPerformance';

/** The request types that live inside this page. Declared, not listed here. */
const WITHIN = HUB_MODULES.filter((m) => m.within === 'requests');

/*
  ⚠️ A Record OVER THE UNION, NOT A LOOKUP WITH A FALLBACK, and adding 'bor'
  proved why: this failed to compile the moment the fourth programme existed.
  A tolerant lookup would have shipped an unstyled row that nobody noticed until
  a member asked why one kind of request looked different.
*/
const KIND_TAG: Record<UnifiedRequest['kind'], string> = {
  lossrun: 'is-application',
  fnol: 'is-endorsement',
  binder: 'is-questionnaire',
  bor: 'is-supplemental',
};

const TABS = [
  { key: 'open', label: 'Outstanding' },
  { key: 'answered', label: 'Answered' },
  { key: 'all', label: 'All' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/**
 * The kind filter, as chips rather than a dropdown.
 *
 * ⚠️ EACH ONE CARRIES ITS COUNT, WHICH IS THE POINT (Josh, 2026-09-05: "quick
 * buttons that sort the dashboard results of each of the 3"). A <select> could
 * filter but could not tell you there are four binders waiting without being
 * opened first — on a dashboard, the number is half the answer.
 */
const KINDS = [
  { key: 'all', label: 'Every kind' },
  { key: 'lossrun', label: 'Loss runs' },
  { key: 'fnol', label: 'Reported losses' },
  { key: 'binder', label: 'Binders' },
] as const;

const shortDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const initialsOf = (name: string): string =>
  String(name ?? '')
    .replace(/\b(inc|llc|ltd|co|corp|company|the|insurance|group)\b\.?/gi, ' ')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('') || '?';

export function Requests(): JSX.Element {
  const navigate = useNavigate();
  /** Set when a request page has just sent something and handed off to here. */
  const receipt = useReceipt();
  const [rows, setRows] = useState<UnifiedRequest[] | null>(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState<TabKey>('open');
  const [kind, setKind] = useState<string>('all');
  const [query, setQuery] = useState('');
  /**
   * Which row has its thread open.
   *
   * ⚠️ THE THREAD LIVES HERE RATHER THAN ON EACH PROGRAMME'S PAGE. Loss Runs has
   * a drawer to put one in; Report a Loss and Binder Requests have no drawer at
   * all, and adding one to each would be three copies of the same list. This is
   * already the page that reads across all three, so it is the one place a
   * thread can be opened for any of them.
   */
  const [openThread, setOpenThread] = useState('');

  const load = useCallback(() => {
    api
      .requestsAll()
      .then((r) => setRows(r.requests))
      .catch((e: unknown) => {
        setRows([]);
        setErr(e instanceof ApiError ? e.message : 'Could not load your requests.');
      });
  }, []);

  useEffect(load, [load]);

  const all = useMemo(() => rows ?? [], [rows]);
  const loading = rows === null;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter(
      (r) =>
        (tab === 'all' || (tab === 'open' ? r.open : !r.open)) &&
        (kind === 'all' || r.kind === kind) &&
        (!q || `${r.insured} ${r.carrier} ${r.id}`.toLowerCase().includes(q)),
    );
  }, [all, tab, kind, query]);

  /**
   * How many of each kind the chips should show.
   *
   * ⚠️ COUNTED AFTER THE TAB AND THE SEARCH, BUT NOT AFTER THE KIND ITSELF —
   * otherwise choosing "Binders" would zero the other two chips and there would
   * be no way to see what you were giving up by switching.
   */
  const kindCounts = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = all.filter(
      (r) =>
        (tab === 'all' || (tab === 'open' ? r.open : !r.open)) &&
        (!q || `${r.insured} ${r.carrier} ${r.id}`.toLowerCase().includes(q)),
    );
    return {
      all: pool.length,
      lossrun: pool.filter((r) => r.kind === 'lossrun').length,
      fnol: pool.filter((r) => r.kind === 'fnol').length,
      binder: pool.filter((r) => r.kind === 'binder').length,
    } as Record<string, number>;
  }, [all, tab, query]);

  const stats = useMemo(() => {
    const open = all.filter((r) => r.open);
    return {
      open: open.length,
      replied: open.filter((r) => r.replied).length,
      late: open.filter((r) => r.urgency === 'late').length,
      soon: open.filter((r) => r.urgency === 'soon').length,
      answered: all.filter((r) => !r.open).length,
    };
  }, [all]);

  return (
    <div className="wrap">
      {/*
        ⚠️ THE "NEW" BUTTONS ARE GENERATED FROM THE REGISTRY, NOT LISTED HERE.
        The three request types came off the rail and live inside this page now,
        so this is their only front door — and a hand-written list of three links
        is exactly the thing that shipped Loss Runs with no way in at all. A
        fourth request type appears here by declaring `within: 'requests'`.
      */}
      <PageHero
        title="Requests Center"
        sub="Everything you have asked a carrier for and not yet got back — loss runs, reported losses and binders in one list."
        action={
          <div className="rq-new">
            {WITHIN.map((m) => (
              <button key={m.id} className="btn btn-primary btn-sm" type="button" onClick={() => navigate(newFormHref(m.href))}>
                + {m.name}
              </button>
            ))}
          </div>
        }
      />

      {/*
        The confirmation for a request sent on another page, shown here because
        this is where it landed. The reference is the routing key for the
        carrier's reply, so it is the part that had to survive the navigation.
      */}
      {receipt && (
        <div className="lr-receipt">
          <p>
            {receipt.headline} Reference <b>{receipt.id}</b>.
          </p>
          {receipt.warn && <p className="is-warn">{receipt.warn}</p>}
        </div>
      )}

      {/*
        Above the stat cards on purpose. Everything below is a report on what is
        outstanding; this is the only thing on the page that is a task, and it
        renders nothing at all when there is nothing to triage.
      */}
      <UnmatchedQueue onFiled={load} />

      {err && <div className="empty">{err}</div>}

      <div className="stat-row">
        <StatCard
          tone="blue"
          icon="M4 5h16v14H4zM8 9h8M8 13h5"
          label="Outstanding"
          value={stats.open}
          note="Waiting on a carrier"
          loading={loading}
        />
        {/* ⚠️ "Needs chasing" AND "Overdue" ARE SEPARATE CARDS BECAUSE THEY ARE
            SEPARATE DECISIONS. One is a nudge; the other is a phone call, and on
            a binder it may be a coverage gap. Folding them together would hide
            the second inside the first. */}
        <StatCard
          tone="amber"
          icon="M12 7v5l3 2M12 3a9 9 0 100 18 9 9 0 000-18z"
          label="Needs chasing"
          value={stats.soon}
          note="Getting close"
          loading={loading}
          slow={stats.soon > 0}
        />
        <StatCard
          tone="amber"
          icon="M12 4l9 16H3zM12 10v4M12 17h.01"
          label="Overdue"
          value={stats.late}
          note={stats.late ? 'Past the point of waiting' : 'Nothing overdue'}
          loading={loading}
          late={stats.late > 0}
        />
        {/*
          ⚠️ ITS OWN CARD, ABOVE "Answered". A request a carrier has replied to is
          the only kind on this page that cannot look after itself: the chase
          schedule handles the silent ones, and nothing at all handles this one
          until a person reads it.
        */}
        <StatCard
          tone="amber"
          icon="M4 5h16v12H7l-3 3z"
          label="Replied"
          value={stats.replied}
          note={stats.replied ? 'A carrier came back — read it' : 'Nothing waiting on you'}
          loading={loading}
          slow={stats.replied > 0}
        />
        <StatCard
          tone="green"
          icon="M12 3a9 9 0 100 18 9 9 0 000-18zM8 12l3 3 5-5"
          label="Answered"
          value={stats.answered}
          note="Carrier came back"
          loading={loading}
        />
      </div>

      <section className="panel lr-table-panel">
        <div className="lr-toolbar">
          <div className="lr-tabs" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={tab === t.key ? 'is-on' : ''}
                onClick={() => setTab(t.key)}
              >
                {t.label}
                {t.key === 'open' && stats.open > 0 && <span className="lr-count">{stats.open}</span>}
              </button>
            ))}
          </div>
          <div className="lr-filters">
            <div className="rq-kinds" role="group" aria-label="Kind of request">
              {KINDS.map((k) => (
                <button
                  key={k.key}
                  type="button"
                  aria-pressed={kind === k.key}
                  className={`${kind === k.key ? 'is-on' : ''} ${kindCounts[k.key] ? '' : 'is-empty'}`}
                  onClick={() => setKind(k.key)}
                >
                  {k.key !== 'all' && <span className={`rq-dot is-${k.key}`} aria-hidden="true" />}
                  {k.label}
                  <span className="rq-n">{loading ? '–' : kindCounts[k.key]}</span>
                </button>
              ))}
            </div>
            <div className="lr-search" style={{ margin: 0 }}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M11 4a7 7 0 100 14 7 7 0 000-14zM20 20l-4-4" />
              </svg>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search insured, carrier, reference…"
                aria-label="Search requests"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <p className="lr-empty">Loading…</p>
        ) : !visible.length ? (
          <p className="lr-empty">
            {all.length ? 'Nothing in this view.' : 'Nothing outstanding.'}
            <span>
              {all.length
                ? 'Try another tab, or clear the filters.'
                : 'Ask a carrier for a loss run, report a loss or request a binder, and it appears here until they answer.'}
            </span>
          </p>
        ) : (
          <>
            <table className="lr-table">
              <thead>
                <tr>
                  <th>Insured</th>
                  <th>Kind</th>
                  <th>Carrier</th>
                  <th>Waiting for</th>
                  <th>Deadline</th>
                  <th>Status</th>
                  <th className="lr-chev" aria-label="Thread" />
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const rowKey = `${r.kind}:${r.id}`;
                  return (
                  <Fragment key={rowKey}>
                  <tr
                    className={r.replied ? 'is-replied-row' : r.open && r.urgency === 'late' ? 'is-late-row' : ''}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(r.href)}
                  >
                    <td>
                      <div className="lr-cell">
                        <span className="lr-avatar" aria-hidden="true">
                          {initialsOf(r.insured)}
                        </span>
                        <div>
                          <b>{r.insured}</b>
                          <span>{r.id}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`tag ${KIND_TAG[r.kind]}`}>{r.kindLabel}</span>
                    </td>
                    <td>
                      <b style={{ fontWeight: 600 }}>{r.carrier}</b>
                      <div className="lr-sub">asked {shortDate(r.createdAt)}</div>
                    </td>
                    <td>
                      <span className="sa-when" style={{ whiteSpace: 'normal' }}>
                        {r.waitingFor}
                      </span>
                    </td>
                    <td>
                      {/* Each programme's own sentence, already decided server-side. */}
                      <b
                        style={{ fontWeight: 600 }}
                        className={
                          r.replied ? 'is-replied' : r.open && r.urgency === 'late' ? 'is-overdue' : ''
                        }
                      >
                        {r.deadline}
                      </b>
                    </td>
                    <td>
                      <span className={`tag ${r.replied ? 'is-moved' : r.open ? 'is-draft' : 'is-online'}`}>
                        {r.replied ? 'Replied' : r.statusLabel}
                      </span>
                    </td>
                    <td className="lr-chev">
                      {/*
                        ⚠️ stopPropagation, BECAUSE THE ROW NAVIGATES. Without it
                        the chevron opens the thread and then immediately leaves
                        the page it opened on, which reads as the button being
                        broken rather than as two things happening.
                      */}
                      <button
                        type="button"
                        aria-expanded={openThread === rowKey}
                        aria-label={openThread === rowKey ? 'Hide the thread' : 'Show the thread'}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenThread(openThread === rowKey ? '' : rowKey);
                        }}
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M9 6l6 6-6 6" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                  {openThread === rowKey && (
                    <tr className="lr-drawer-row">
                      <td colSpan={7}>
                        <RequestThread reference={r.id} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
            <p className="lr-count-line">
              Showing {visible.length} of {all.length} request{all.length === 1 ? '' : 's'}
            </p>
          </>
        )}
      </section>

      {/*
        Below the outstanding list, because it answers a slower question. The
        list is what to do today; this is who to expect it from.
      */}
      <CarrierPerformance />

      <section className="panel lr-about">
        <div>
          <h3>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 3a9 9 0 100 18 9 9 0 000-18zM12 11v5M12 8h.01" />
            </svg>
            What counts as a request
          </h3>
          <p>
            Anything you have asked a carrier for and are waiting on: loss runs from a prior
            carrier, a claim number after reporting a loss, a binder from the underwriter who
            quoted. Each still has its own page for creating one — this is where you see them all
            at once.
          </p>
          <p className="lr-about-note">
            The Submission Portal and Policy Upload are not here. Those send work to Agents
            Alliance rather than asking a market for something back.
          </p>
        </div>
        <div>
          <h3>Why the deadlines read differently</h3>
          <ul>
            <li>A loss run gets urgent by waiting — the clock counts up</li>
            <li>A reported loss is urgent until a claim number comes back</li>
            <li>A binder counts down to inception, so a new one can outrank an old one</li>
            <li>Rows group by how urgent, not by one shared number</li>
          </ul>
        </div>
      </section>
    </div>
  );
}
